'use client';

import isEqual from 'lodash/isEqual';
import type { ReactElement, ReactNode } from 'react';
import { createContext, useEffect, useRef, useState } from 'react';
import type {
  AnalyticsServiceAsyncMethod,
  IAnalytics,
} from './Analytics.types';
import type { IAnalyticsServiceParams } from './AnalyticsService.types';
import type { AnalyticsServices } from './AnalyticsServices.registry';
import { AnalyticsServicesRegistry } from './AnalyticsServices.registry';
import type { IUseQueue } from './utils/useQueue';
import { useQueue } from './utils/useQueue';

type IAnalyticsContext = {
  services: IAnalytics[];
  queue: IUseQueue<AnalyticsServiceAsyncMethod>;
  isInitialized: boolean;
};

export const AnalyticsContext = createContext<IAnalyticsContext | null>(null);

const instantiateServices = (
  enabledServices: IAnalyticsProviderProps['enabledServices']
) =>
  enabledServices.map((service) =>
    typeof service === 'string'
      ? AnalyticsServicesRegistry[service]()
      : AnalyticsServicesRegistry[service.service](service.params)
  );

type IAnalyticsProviderProps = {
  children: ReactNode | ReactNode[];
  enabledServices: (
    | AnalyticsServices
    | { service: AnalyticsServices; params: IAnalyticsServiceParams }
  )[];
};

/**
 * @usage
 * ```tsx
 * <AnalyticsProvider enabledServices={['gtag']}>
 *   <App />
 * </AnalyticsProvider>
 * ```
 */
export const AnalyticsProvider = ({
  children,
  enabledServices,
}: IAnalyticsProviderProps): ReactElement => {
  const [services, setServices] = useState<IAnalytics[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const queue = useQueue<AnalyticsServiceAsyncMethod>();

  const currentEnabledServicesRef = useRef<
    IAnalyticsProviderProps['enabledServices'] | null
  >(null);

  useEffect(() => {
    const initializeServices = async () => {
      if (!isEqual(enabledServices, currentEnabledServicesRef.current)) {
        const servicesInst = instantiateServices(enabledServices);
        setServices(servicesInst);

        await Promise.allSettled(
          servicesInst.map(async (service) => {
            try {
              await service.waitForInitialization();
            } catch (e) {
              console.warn(
                `${service.name} Analytics Service Object failed: ${
                  e instanceof Error ? e.message : e
                }`
              );
              return;
            }
          })
        );

        setIsInitialized(true);
      }
    };

    initializeServices();
    currentEnabledServicesRef.current = enabledServices;
  }, [enabledServices]);

  return (
    <AnalyticsContext.Provider value={{ services, queue, isInitialized }}>
      {children}
    </AnalyticsContext.Provider>
  );
};
